<template>
  <div
    ref="drawerRef"
    class="flex notification-drawer relative"
    :class="store.isOpen ? 'notification-open' : `notification-close-${store.edge}`"
  >
    <!-- Where the panel's modal dialogs are teleported. The drawer is transformed,
         so their `fixed` overlay covers the drawer — strip and panel — and not
         the whole window with the tabs' corridor; being drawer UI, it also keeps
         the window solid under the click-through reconciliation (CO-2658). -->
    <div id="notification-dialogs" />
    <!-- The tab hangs from a zero-width anchor on the panel's outer edge (the
         button is absolutely positioned, so the wrapper has no box of its
         own). The wrapper must NOT be given a width: every pixel of it counts
         as drawer UI for the click-through reconciliation, so a wide wrapper
         makes the transparent corridor solid and blocks clicks to the LGO.
         Hovering the button itself drives the slide-out and does not
         oscillate: the tab grows towards the cursor when sliding out, and
         retracts away from it. The window is always wide enough for the fully
         slid-out tab (resizing on hover repaints the transparent window on
         Windows and invalidates the hover mid-flight). The tab only rounds its
         outer corners and stops 8px short of fully sliding out, so it reads
         as attached to the panel. -->
    <div
      v-if="canReadCard && store.isOpen && (!!item || !!error)"
      class="absolute top-[88px] h-10 group"
      :class="store.edge === 'left' ? 'left-[315px]' : 'right-[315px]'"
    >
      <button
        class="absolute inset-y-0 w-max transition-transform duration-300 flex items-center gap-[12px] bg-white group-hover:bg-green-light shadow-custom py-2 text-sm font-semibold text-primary whitespace-nowrap"
        :class="{
          'cursor-wait': detecting,
          'cursor-pointer': !detecting,
          'rounded-l-lg right-0 pl-[8px] pr-[24px] translate-x-[calc(100%_-_36px)] group-hover:translate-x-[8px]': store.edge === 'right',
          'rounded-r-lg left-0 pr-[8px] pl-[24px] flex-row-reverse -translate-x-[calc(100%_-_36px)] group-hover:-translate-x-[8px]': store.edge === 'left',
        }"
        title="Lire la carte Vitale"
        @click.stop="emit('read-vitale-card')"
      >
        <img
          src="@/assets/images/carte-vitale.png"
          alt=""
          class="w-[20px] object-contain"
        >
        Lire la carte Vitale
      </button>
    </div>
    <!-- The ADAC tab hangs one row below the Vitale one, and is a button as
         that one is: it slides out on hover and asks ADAC on a click. It says
         nothing of the answer, which is in the ADAC card at the top of the
         programmes. It only stays slid out as its flyout's header, when no
         check can be made. -->
    <div
      v-if="canShowAdac && store.isOpen && (!!item || !!error)"
      class="absolute top-[136px] h-10 group"
      :class="store.edge === 'left' ? 'left-[315px]' : 'right-[315px]'"
    >
      <button
        class="absolute inset-y-0 transition-transform duration-300 flex items-center gap-[12px] bg-white group-hover:bg-green-light py-2 text-sm font-semibold text-primary whitespace-nowrap"
        :class="{
          'cursor-wait': adacState === 'running',
          'cursor-pointer': adacState !== 'running',
          'w-max shadow-custom': !adacFlyout,
          'w-[248px] border border-b-0 border-gray-200': adacFlyout,
          'rounded-l-lg right-0 pl-[8px] pr-[24px]': store.edge === 'right',
          'rounded-bl-none': store.edge === 'right' && adacFlyout,
          'translate-x-[calc(100%_-_36px)] group-hover:translate-x-[8px]': store.edge === 'right' && !adacTabOut,
          'translate-x-[8px]': store.edge === 'right' && adacTabOut,
          'rounded-r-lg left-0 pr-[8px] pl-[24px] flex-row-reverse': store.edge === 'left',
          'rounded-br-none': store.edge === 'left' && adacFlyout,
          '-translate-x-[calc(100%_-_36px)] group-hover:-translate-x-[8px]': store.edge === 'left' && !adacTabOut,
          '-translate-x-[8px]': store.edge === 'left' && adacTabOut,
        }"
        title="Vérifier avec ADAC"
        @click.stop="checkAdac"
      >
        <img
          src="@/assets/images/adac.png"
          alt=""
          class="w-[20px] object-contain"
        >
        Vérifier avec ADAC
      </button>
    </div>
    <!-- The flyout the tab unfolds into when no check can be made: why, and
         the one thing to do about it, beside the panel rather than in it. It
         lives where the tabs slide out — the transparent corridor plus the
         empty part of the strip column — and tucks 8px under the panel like
         the tab it continues (the panel, `relative` and later in the DOM,
         paints over both). Its width is the corridor's, less a margin: the
         window is fixed at NOTIFICATION_WINDOW_WIDTH and never widens. Inside
         the drawer subtree, so the click-through reconciliation makes it
         solid; marked, so the height measures include it and the window grows
         to hold it. -->
    <div
      v-if="adacFlyout && store.isOpen && !!item"
      ref="adacFlyoutRef"
      data-notification-flyout
      class="absolute top-[176px] w-[248px] max-h-[420px] overflow-y-auto notification-scroll bg-white shadow-custom border border-gray-200 p-3"
      :class="store.edge === 'left' ? 'left-[307px] rounded-br-lg' : 'right-[307px] rounded-bl-lg'"
      @mouseenter="store.windowHoverChange(true)"
      @mouseleave="store.windowHoverChange(false)"
    >
      <div class="text-sm">
        <p class="font-semibold text-gray-dark">
          {{ adacConfigured ? 'Aucun poste passerelle connu' : 'Configuration ADAC à terminer' }}
        </p>
        <p class="mt-1 text-xs leading-snug text-gray-dark/70">
          L'appel ADAC part du poste qui tient la carte CPS et son code porteur.
          {{ adacConfigured ? 'Ce poste ne se voit pas passerelle, et n\'en connaît aucun autre.' : 'Le module attend la fin de sa configuration, depuis la page du programme.' }}
        </p>
        <div class="mt-2">
          <button
            type="button"
            class="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-primary underline-offset-2 hover:underline disabled:opacity-50"
            :disabled="adacLooking"
            @click="lookForGateway"
          >
            <LoaderCircle
              v-if="adacLooking"
              class="animate-spin"
              :size="12"
            />
            <RefreshCw
              v-else
              :size="12"
            />
            Chercher la passerelle
          </button>
        </div>
      </div>
    </div>
    <!-- The strip column sits between the panel and the pinned screen edge:
         last flex child when the window is pinned to the left edge. -->
    <div
      ref="stripColumnRef"
      data-notification-strip
      :style="{minHeight: `${collapsedStripHeight}px`}"
      class="shrink-0 flex flex-col items-center gap-3"
      :class="{'order-last': store.edge === 'left'}"
    >
      <NotificationDetectionStrip
        :item="item"
        :detecting="detecting"
        :is-open="store.isOpen"
        :edge="store.edge"
        @toggle="store.toggleNotification"
      />
      <button
        v-if="canReadCard && !store.isOpen && !detecting"
        class="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-custom border border-gray-200 hover:bg-green-light cursor-pointer"
        title="Lire la carte Vitale"
        @click.stop="emit('read-vitale-card')"
      >
        <img
          src="@/assets/images/carte-vitale.png"
          alt="Lire la carte Vitale"
          class="w-6 object-contain"
        >
      </button>
      <!-- The ADAC entry carries its name inside the round, and the spinner
           the handle uses in its place while the check runs: the pharmacist who
           started it reads that it is under way without opening anything,
           whatever the wallpaper behind. -->
      <div
        v-if="canShowAdac && !store.isOpen && !detecting"
        class="relative w-10 h-10"
      >
        <button
          class="relative w-10 h-10 flex flex-col items-center justify-center bg-white rounded-full shadow-custom border border-gray-200 hover:bg-green-light"
          :class="adacState === 'running' ? 'cursor-wait' : 'cursor-pointer'"
          :title="adacLabel"
          @click.stop="checkAdac"
        >
          <span
            v-if="adacPastille"
            class="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white"
            :class="adacPastille"
          />
          <BaseSpinner
            v-if="adacState === 'running'"
            color="primary"
            size="button"
          />
          <template v-else>
            <img
              src="@/assets/images/adac.png"
              :alt="adacLabel"
              class="w-[18px] object-contain"
            >
            <span class="text-[7px] font-bold leading-none text-primary">ADAC</span>
          </template>
        </button>
      </div>
    </div>
    <!-- `relative` keeps the panel painted above the Vitale card tab, whose
         hidden part slides underneath (a negative z-index would instead put
         the tab below the strip column's stretched hit-box, breaking hover). -->
    <div
      v-if="store.shouldShowPanel"
      class="notification-scroll relative flex-1 bg-white border-b border-gray-200 w-[315px] max-h-screen overflow-y-auto overflow-x-hidden"
      :class="store.edge === 'left' ? 'rounded-br-xl border-r' : 'rounded-bl-xl border-l'"
      @mouseenter="store.windowHoverChange(true)"
      @mouseleave="store.windowHoverChange(false)"
    >
      <!-- data-notification-content: measured by useNotification to size the window
           on the natural content height — the panel itself is capped at max-h-screen
           (the *current* window height), so measuring it can never grow the window. -->
      <div ref="panelContent" data-notification-content>
        <notification-detection-customer-header
          class="sticky top-0 z-10 select-none notification-drag-handle"
          :item="item"
          :detecting="detecting"
          :error="error"
          :selected-consumer="selectedConsumer"
          :alternate-consumer="alternateConsumer"
          @pointerdown="headerDrag.onPointerDown"
          @switch-consumer="switchConsumer"
          @show-customer="showCustomer"
          @navigate-sms="navigateCertification"
          @navigate-surveys="navigateSurveys"
          @navigate-appointment="navigateAppointment"
          @navigate-knowledgeables="navigateKnowledgeables"
        />
        <div v-if="item && !detecting" class="pt-6 px-3 space-y-3 pb-3">
          <button
            v-if="hasCandidateChoice"
            class="flex items-center gap-1 text-xs text-gray-600 cursor-pointer"
            @click="$emit('back-to-candidates')"
          >
            <Icon name="mdi-arrow-left" :size="14" color="currentColor" />
            Ce n'est pas le bon patient ? Revenir au choix
          </button>
          <NotificationDetectionNoRecentSync />
          <NotificationDetectionFraudWarning :report="item.fraud_report" />
          <BirthdayVoucherReminder
            v-if="item.customer?.birthday_gift_voucher"
            :voucher="item.customer.birthday_gift_voucher"
            compact
            @scan="scanBirthdayGift"
          />
          <div v-if="item.nextAppointment">
            <p>
              <Icon name="mdi-calendar-month" class="text-primary pr-1" />
              <span class="text-sm text-primary font-semibold">{{ item.nextAppointment.name }}</span>
              <span class="text-sm font-medium"> le {{
                dateHelper.shortFormat(item.nextAppointment.start_at, 'dddd LL hh:mm')
              }}</span>
            </p>
            <span v-if="item.nextAppointment.total_incoming - 1 > 0" class="text-sm text-primary font-semibold pl-5">
              + {{ item.nextAppointment.total_incoming - 1 }} à venir
            </span>
          </div>
          <NotificationDetectionPolls :item="item" />
          <notification-detection-programs
            v-if="item.customer"
            :item="item"
            :selected-consumer-key="selectedConsumer?.key ?? null"
            :hidden-program-ids="adacHiddenProgramIds"
            @resize="handleNotificationResize"
          >
            <template
              v-if="adacCardShown && adacProgram && adacOnDetectedConsumer"
              #leading
            >
              <NotificationProgramAdacCard
                :key="adacCardKey"
                :program="adacProgram"
                :state="adacState"
                :checks="adacChecks"
                :via="adacVia"
                :refusal="adacRefusal"
                :checked-at="adacCheckedAt"
                @retry="checkAdac"
                @check-with-nir="checkAdacWith"
              />
            </template>
          </notification-detection-programs>
          <notification-detection-memberships
            v-if="item.customer"
            :item="item"
            @link-membership="attachLoyaltyCard"
            @show-membership="showMembership"
            @show-reward="showReward"
            @resize="handleNotificationResize"
          />
        </div>
        <div v-if="detecting" class="pt-6 px-3 space-y-3 pb-3 text-sm flex gap-2 items-center">
          <CloudDownload :size="12" /> Récupération des programmes ...
        </div>
        <!-- pb (padding), not mb: the window height is sized on the measured
             content box, and a last-child bottom margin escapes the measure
             (margin collapsing), clipping the banner against the window edge. -->
        <div v-if="error && !item && !detecting" class="mt-[10px] min-h-[34px] pb-3 px-3 flex items-center">
          <NotificationDetectionCandidatePicker
            v-if="error.candidates?.length"
            :error="error"
            @pick="(candidate) => $emit('pick-candidate', candidate)"
          />
          <NotificationDetectionError v-else :error="error" />
        </div>
        <div v-if="canReadCard && !item && !error && !detecting" class="pt-6 px-3 pb-3">
          <button
            class="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white shadow-custom py-2.5 text-sm font-semibold text-primary hover:bg-green-light cursor-pointer"
            @click="emit('read-vitale-card')"
          >
            <img
              src="@/assets/images/carte-vitale.png"
              alt=""
              class="w-5 object-contain"
            >
            Lire la carte Vitale
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from "vue";
import '@/notification/components/notification-detection/notification-detection.css';
import {extensionApi, loyaltyCardMembershipApi} from '@/container';
import Extension, {ExtensionType} from "@/core/interfaces/extension/Extension";
import ExtensionService from "@/extension/services/ExtensionService";
import NotificationDetectionPrograms
    from '@/notification/components/notification-detection/programs/NotificationDetectionPrograms.vue';
import NotificationDetectionMemberships
    from '@/notification/components/notification-detection/loyalty-cards/NotificationDetectionMemberships.vue';
import NotificationDetectionCustomerHeader
    from '@/notification/components/notification-detection/NotificationDetectionCustomerHeader.vue';
import NotificationDetectionStrip
    from "@/notification/components/notification-detection/NotificationDetectionStrip.vue";
import {
    DetectionCandidate,
    DetectionErrorProps,
    DetectionLoyaltyCard,
    DetectionNotificationProps,
    DetectionPatient
} from "@/notification/interfaces";
import Customer from "@/customer/interfaces/Customer";
import {APPOINTMENT_SHORTCUT_QUERY_KEY} from "@/appointment/appointmentShortcut";
import Consumer from "@/core/interfaces/consumers/Consumer";
import {DETECTION_COLLAPSED_HEIGHT, DETECTION_HEADER_HEIGHT, useNotificationStore} from "@/stores/notification.store";
import NotificationDetectionFraudWarning
    from "@/notification/components/notification-detection/NotificationDetectionFraudWarning.vue";
import Icon from "@/core/components/icon/Icon.vue";
import dateHelper from "@/core/helpers/dateHelper";
import NotificationDetectionPolls
    from "@/notification/components/notification-detection/NotificationDetectionPolls.vue";
import {CloudDownload} from "@lucide/vue";
import NotificationDetectionNoRecentSync
    from "@/notification/components/notification-detection/NotificationDetectionNoRecentSync.vue";
import BirthdayVoucherReminder from "@/birthday-gift/components/BirthdayVoucherReminder.vue";
import {BIRTHDAY_GIFT_SCAN_QUERY_KEY} from "@/birthday-gift/birthdayGiftScanShortcut";
import NotificationDetectionError
    from "@/notification/components/notification-detection/NotificationDetectionError.vue";
import NotificationDetectionCandidatePicker
    from "@/notification/components/notification-detection/NotificationDetectionCandidatePicker.vue";
import useNotificationDrag from "@/notification/composables/useNotificationDrag";
import {
    collapsedAnchoredBounds,
    COLLAPSED_WIDTH,
    NOTIFICATION_WINDOW_WIDTH,
} from "@/notification/services/notificationWindowPlacement";
import {
    supportsAdac,
    supportsClickThrough,
    supportsOnDemandCardRead,
    supportsWindowPlacement
} from "@/core/services/shellCapabilities";
import {LoaderCircle} from "@lucide/vue";
import BaseSpinner from "@/core/components/base/spinner/BaseSpinner.vue";
import {useCpsStore} from "@/stores/cps.store";
import useAdac, {ADAC_GIST_COLOURS, adacRefusalMessage} from "@/amelipro/composables/useAdac";
import {portalNir} from "@/amelipro/nir";
import useCpsLocator from "@/amelipro/composables/useCpsLocator";
import {RefreshCw} from "@lucide/vue";
import NotificationProgramAdacCard
    from "@/notification/components/notification-detection/programs/NotificationProgramAdacCard.vue";

const props = defineProps<{
    item: DetectionNotificationProps | null
    detecting: DetectionPatient | null
    error: DetectionErrorProps | null
    /** Whether the displayed patient was picked among ambiguous candidates (a « back » link is offered). */
    hasCandidateChoice?: boolean
}>();

const emit = defineEmits<{
    'pick-candidate': [candidate: DetectionCandidate];
    'back-to-candidates': [];
    'read-vitale-card': [];
}>();

const store = useNotificationStore();
const stripColumnRef = ref<HTMLElement | null>(null);

/** The on-demand read UI is hidden on shells that cannot read the card outside the polling loop. */
const canReadCard = supportsOnDemandCardRead();

/** The ADAC entries are hidden on shells that cannot carry the module. */
const canShowAdac = supportsAdac();

/*
 * The ADAC check, which the composable runs and holds: the categories are
 * asked for here and answered either by this post's own card or by the
 * pharmacy's gateway, and the refs are destructured so the template reads
 * them directly.
 */
const {
    state: adacState,
    checks: adacChecks,
    via: adacVia,
    refusal: adacRefusal,
    gist: adacGist,
    checkedAt: adacCheckedAt,
    run: runAdac,
    reset: resetAdac,
} = useAdac();

/**
 * The ADAC module, as the pharmacy has it — or null when it is not offered.
 *
 * Read from the API rather than from the extension store, which this window
 * never loads, and whatever the shell: the entry shows on every post, and a
 * click on a module still to activate or to configure opens its page.
 */
const adacExtension = ref<Extension | null>(null);

/** Whether the pharmacy has been through the ADAC configuration. */
const adacConfigured = ref(false);

/** Which read of the module's state is the latest: an older answer landing late is dropped. */
let adacReadRound = 0;

/**
 * The patient at the counter: the customer and the person detected in their
 * file. The item is refreshed at every detection, the same patient's
 * included; ADAC follows the patient, not the item.
 */
const patientKey = computed(() => {
    const item = props.item;

    if (!item) {
        return null;
    }

    if (item.customer) {
        return `${item.customer.id}:${item.detectedConsumer?.key ?? ''}`;
    }

    // No file matched: the patient as the detection read them, so that two
    // such patients never share an answer.
    const {nir, lastName, firstName, birthDate} = item.patient ?? {};

    return `read:${nir ?? ''}:${lastName ?? ''}:${firstName ?? ''}:${birthDate ?? ''}`;
});

/** The patient the automatic check last ran for: once per patient at the counter. */
let autoCheckedPatient: string | null = null;

/** The patient the module's state was last read for: the automatic check waits for its own. */
const adacReadFor = ref<string | null | undefined>(undefined);

/** The read under way, which a click arriving before its answer waits for. */
let adacReading: Promise<void> | null = null;

const readAdacConfiguration = (): Promise<void> => {
    const round = ++adacReadRound;
    const patient = patientKey.value;

    adacReading = (async () => {
        try {
            const extensions = await extensionApi().index({types: [ExtensionType.PROGRAM_BUNDLE, ExtensionType.PACK]});

            if (round === adacReadRound) {
                adacExtension.value = extensions.find((extension) => extension.configurator === 'adac') ?? null;
                adacConfigured.value = !!adacExtension.value?.enabled && !!adacExtension.value?.configured;
            }
        } catch {
            if (round === adacReadRound) {
                adacConfigured.value = false;
            }
        } finally {
            if (round === adacReadRound) {
                adacReadFor.value = patient;
                adacReading = null;
            }
        }
    })();

    return adacReading;
};

onMounted(readAdacConfiguration);

/**
 * The ADAC card's identity, one per detection: the card is rebuilt for every
 * patient, so that a NIR typed for the previous one, or its detail left open,
 * never carries over to the next.
 */
const adacCardKey = ref(0);

// This window lives as long as Connect does: the module's state is read again
// for every patient, so a module configured, disabled or set up anew since is
// what the next patient's entry follows.
//
// The check is dropped with the patient it was about, and the ADAC card with
// it. For the new one, it starts on its own only when a targeting the
// pharmacy checked holds them — see autoCheckAdac — and otherwise waits for
// the pharmacist to ask. The same patient detected again keeps theirs; one
// who comes back after another — or after a detection error — is a new visit:
// nothing known from before, the module's state included, is reused.
watch(patientKey, () => {
    autoCheckedPatient = null;
    adacReadFor.value = undefined;
    readAdacConfiguration();
    resetAdac();
    adacCardKey.value += 1;
});

const cpsStore = useCpsStore();

/**
 * The ADAC entry shows on every post whose shell can carry the module,
 * whatever the module's state: it is how the pharmacy learns the module
 * exists. It asks ADAC once the pharmacy has configured it, and a gateway this
 * post can reach holds the card — itself, or another post seen on the channel.
 * Until then a click leads to what is missing: the module's page to activate
 * or configure it, or the flyout that says no gateway is known.
 */
const locator = useCpsLocator();

/** Whether the module can run from this post: the shell carries it, and the pharmacy configured it. */
const adacReady = computed(() => supportsAdac() && adacConfigured.value);

/** Whether a gateway can be reached: this post, or another one seen on the channel. */
const adacReachable = computed(() => cpsStore.isLocalGateway || cpsStore.remoteGateway !== null);

const canCheckAdac = computed(() => adacReady.value && adacReachable.value);

/**
 * Open the module's page in the main window, which leads where the module
 * stands: its store sheet to activate it, its configuration — or the store,
 * when the pharmacy is not offered the module at all.
 */
const showAdacExtension = (): void => {
    window.ipcRenderer.send('to-main-window', 'show-extension', adacExtension.value?.id ?? null);
};

/** TEMPORARY — whether a fresh look for the gateway is under way. */
const adacLooking = ref(false);

/**
 * TEMPORARY — look for the gateway again, from the reader, and ask the other
 * posts to show themselves.
 */
const lookForGateway = async (): Promise<void> => {
    adacLooking.value = true;

    try {
        await locator.lookForGateway();
    } finally {
        adacLooking.value = false;
    }
};

/** A round entry under the strip: 35px at the app's 14px root font, plus its gap. */
const STRIP_ENTRY_HEIGHT = 46;

/**
 * The height the handle column needs once collapsed.
 *
 * Reserved at all times, and not measured: the window is sized while the panel
 * is open, when these entries are not rendered at all, and a shell that clicks
 * through never resizes on collapse. Without this the last entry ends up
 * outside the window and gets cut.
 */
const collapsedStripHeight = computed(() => DETECTION_HEADER_HEIGHT
    + (canReadCard ? STRIP_ENTRY_HEIGHT : 0)
    + (canShowAdac ? STRIP_ENTRY_HEIGHT : 0));

/**
 * What the ADAC entry reads, at rest and along the way.
 *
 * The entry is all a pharmacist sees while the panel is collapsed, so it
 * carries the gist the ADAC card says, and not just « done ».
 */
const adacLabel = computed(() => {
    if (adacState.value === 'idle' && !canCheckAdac.value) {
        if (!adacExtension.value?.enabled) {
            return 'ADAC : activer le module';
        }

        if (!adacConfigured.value) {
            return 'ADAC : configuration à terminer';
        }

        return 'ADAC : aucune passerelle';
    }

    switch (adacState.value) {
        case 'running':
            return 'Vérification ADAC…';
        case 'done':
            return adacGist.value.message;
        case 'failed':
            return adacRefusalMessage(adacRefusal.value);
        default:
            return 'Vérifier sur ADAC';
    }
});

/**
 * The gist at a glance, on the collapsed entry.
 *
 * Orange when ADAC itself flagged a category, green only for a complete
 * answer with nothing reimbursed, neutral otherwise — the same reading as the
 * ADAC card — and nothing at all until there is an answer: a running check
 * has its spinner, a refused one has its words.
 */
const adacPastille = computed(() => {
    if (adacState.value === 'idle' && !canCheckAdac.value) {
        return 'bg-gray';
    }

    if (adacState.value !== 'done') {
        return null;
    }

    return ADAC_GIST_COLOURS[adacGist.value.tone];
});

/** Whether the ADAC tab is unfolded into its flyout. */
const adacFlyout = ref(false);

/** The flyout element, for the height sync. */
const adacFlyoutRef = ref<HTMLElement | null>(null);

/** The ADAC programme, as the detection lists it among a patient's programmes. */
const adacProgramId = computed(() => ExtensionService.supportProgramOf(adacExtension.value)?.id ?? null);

/**
 * The detected patient, when a checked targeting holds them: the detection
 * lists the ADAC programme among a patient's programmes only for the
 * targetings the pharmacy checked. Only the detected person counts — the
 * customer when no one more precise was matched — since ADAC is asked about
 * their NIR: a beneficiary's targeting must not trigger a check on the
 * insured customer.
 */
const adacTargetConsumer = computed(() => {
    const id = adacProgramId.value;
    const consumer = props.item?.detectedConsumer ?? props.item?.customer ?? null;

    if (!id || !consumer) {
        return null;
    }

    return consumer.programs?.some(program => program.id === id) ? consumer : null;
});

/** Whether the patient on screen is one ADAC is asked about on its own. */
const adacTargeted = computed(() => !!adacTargetConsumer.value);

/**
 * The ADAC programme, for its card: the patient's own when a targeting holds
 * them, else the module's — a patient asked about at a click is in no
 * targeting, and the card wears the programme's name and logo all the same.
 */
const adacProgram = computed(() => adacTargetConsumer.value?.programs
    ?.find(program => program.id === adacProgramId.value)
    ?? ExtensionService.supportProgramOf(adacExtension.value));

/**
 * Whether the ADAC card heads the programmes: once ADAC is asked about the
 * patient on screen — and already for a patient a checked targeting holds,
 * whose check is about to start on its own.
 */
const adacCardShown = computed(() => !!props.item
    && (adacState.value !== 'idle' || (adacTargeted.value && canCheckAdac.value)));

/**
 * The programmes the list leaves out: the ADAC one, always — its card stands
 * for it when ADAC is asked, and it has nothing else to show.
 */
const adacHiddenProgramIds = computed(() => (adacProgramId.value ? [adacProgramId.value] : []));

/** Whether the tab stays slid out on its own: as the flyout's header. */
const adacTabOut = computed(() => adacFlyout.value);

// The flyout only says why no check can be made: a check that starts, or a
// new patient, folds it.
watch(adacState, () => {
    adacFlyout.value = false;
});

// Closing the drawer takes the tabs with it, and the flyout along.
watch(() => store.isOpen, (open) => {
    if (!open) {
        adacFlyout.value = false;
    }
});

/**
 * The NIR ADAC is asked about: the one the detection read, when it is one,
 * else the one the HDS keeps on the detected patient's file. An emulated
 * detection carries the insured identifier in its place and an OCR one often
 * nothing, while the file has it: a NIR the pharmacy already holds is never
 * asked of the pharmacist. When neither is usable — no patient identified, a
 * file without a real NIR — the detection's own value is passed on, so the
 * refusal names what it gave and the flyout offers its field.
 */
const adacNir = computed(() => portalNir(props.item?.patient?.nir)
    || props.item?.detectedConsumer?.nir
    || props.item?.patient?.nir);

/**
 * Ask ADAC about the patient on screen — or, once it has answered, show what
 * it said.
 *
 * Opening the session is no longer the point — it is what the composable does
 * on the way, silently, when the portal has dropped it. What this reports is
 * the patient's reimbursements, category by category, and the ADAC card at
 * the top of the programmes is where they are read: the tab starts the check,
 * then becomes the way back to its result.
 */
const checkAdac = async (): Promise<void> => {
    if (adacState.value === 'running') {
        return;
    }

    // The module's state is being read again for this patient: its answer
    // decides, not the previous patient's. The click is for the patient on
    // screen now: one detected meanwhile is not asked about on its behalf.
    const patient = patientKey.value;

    if (adacReading) {
        await adacReading;

        if (patientKey.value !== patient) {
            return;
        }
    }

    // ADAC is asked about the detected person: the panel goes back to them
    // first, so that the answer shows under the right name.
    if (!adacOnDetectedConsumer.value) {
        selectedConsumerKey.value = props.item?.detectedConsumer?.key ?? null;
    }

    // A module still to activate or to configure — or a shell that cannot
    // carry it yet, whose own page says so — opens the module's page. So does
    // a click with no patient on screen, after a detection error: there is
    // nobody to ask ADAC about.
    if (!adacReady.value || !props.item) {
        showAdacExtension();

        return;
    }

    // The answer is in: from the collapsed handle, the drawer opens on it,
    // whether a gateway is still reachable or not — reaching one is for a
    // new request only. So does a refusal: the card is where the NIR is
    // typed and the retry asked for, and asking again blind would only fail
    // the same way.
    if (adacState.value === 'done' || (adacState.value === 'failed' && !store.isOpen)) {
        if (!store.isOpen) {
            store.toggleNotification();
        }

        return;
    }

    // No gateway to ask: the flyout says why instead of a check that could
    // only fail.
    if (!canCheckAdac.value) {
        if (!store.isOpen) {
            store.toggleNotification();
            await nextTick();
            adacFlyout.value = true;

            return;
        }

        adacFlyout.value = !adacFlyout.value;

        return;
    }

    await runAdac(adacNir.value, undefined, {emulated: props.item?.patient?.emulated === true});
};

/**
 * Ask ADAC with a NIR the pharmacist read off the LGO, when neither the
 * detection nor the patient's file gave one the portal would take. The same
 * normalization and validation apply as to a detected one; the value lives in
 * the run and nowhere else.
 *
 * @param nir What the pharmacist typed.
 */
const checkAdacWith = (nir: string): Promise<void> => runAdac(nir);

/**
 * Ask ADAC on its own about a patient a checked targeting holds.
 *
 * The pharmacy chose it, targeting by targeting, in the module's
 * configuration: the counter asks every category, as a click on the entry
 * would, as soon as the patient is known to be held, ADAC can be reached, and
 * nothing was asked for them yet. Once per patient at the counter: the same
 * patient detected again — the LGO read twice, an OCR that wavers — keeps the
 * answer they got, rather than asking the portal over and over.
 */
const autoCheckAdac = (): void => {
    if (!props.item || patientKey.value === autoCheckedPatient) {
        return;
    }

    // Until the module's state is read for this patient, what is known is the
    // previous one's: a module disabled since must not be asked on its behalf.
    if (adacReadFor.value !== patientKey.value) {
        return;
    }

    if (!adacTargeted.value || !canCheckAdac.value || adacState.value !== 'idle') {
        return;
    }

    autoCheckedPatient = patientKey.value;
    runAdac(adacNir.value, undefined, {emulated: props.item.patient?.emulated === true});
};

// What decides comes in any order: the patient, the module's state read from
// the API, the gateway seen on the pharmacy channel.
watch([patientKey, adacTargeted, canCheckAdac, adacReadFor], autoCheckAdac, {flush: 'post'});

/**
 * The panel header doubles as a drag handle for the notification window.
 * The composable itself holds the auto-close countdown during the drag.
 */
const headerDrag = useNotificationDrag(() => store.isOpen);

// Keep the Electron window sized to the panel content, and let the panel scroll past the
// screen. The initial measurement is often too small (first detection: images not cached
// yet, text painted with fallback fonts), which left the last items clipped with no scroll.
//
// A ResizeObserver reacts to any late growth (fonts, images, async data). `scrollHeight`
// reports the *natural* content height even while the panel is capped at `max-h-screen` and
// scrolling, so we can grow the window to min(content, screen) — beyond which the panel
// itself scrolls (the document can't: <html> is overflow:hidden).
const SCREEN_MARGIN = 40;
/** Room kept under the ADAC flyout, so its shadow is not the last pixel of the window. */
const FLYOUT_MARGIN = 8;
const panelContent = ref<HTMLElement | null>(null);
let panelResizeObserver: ResizeObserver | null = null;
let flyoutResizeObserver: ResizeObserver | null = null;
let stripResizeObserver: ResizeObserver | null = null;
let lastSentHeight = 0;

const syncWindowHeight = () => {
    const content = panelContent.value;

    if (!content) {
        return;
    }

    // Measure the uncapped content wrapper (not the max-h-screen panel, whose box stays fixed
    // once content overflows and thus never reports growth to the ResizeObserver). The ADAC
    // flyout hangs beside the panel, absolutely positioned in the drawer: it is not in that
    // wrapper's box, and a window sized on the wrapper alone would cut it at the bottom. So
    // would a short panel cut the handle column.
    const flyout = adacFlyoutRef.value;
    const flyoutBottom = flyout ? flyout.offsetTop + flyout.offsetHeight + FLYOUT_MARGIN : 0;
    const desiredHeight = Math.min(
        Math.max(content.offsetHeight, flyoutBottom, stripColumnRef.value?.offsetHeight ?? 0),
        window.screen.availHeight - SCREEN_MARGIN
    );

    // Skip no-op resizes: the main-process resize animates, which would otherwise re-fire the
    // observer with the same target height and fight the animation.
    if (desiredHeight === lastSentHeight) {
        return;
    }

    lastSentHeight = desiredHeight;
    window.ipcRenderer.resizeNotificationWindow(desiredHeight);
};

const handleNotificationResize = () => syncWindowHeight();

watch(panelContent, (element) => {
    panelResizeObserver?.disconnect();

    if (element) {
        // The panel remounts on every drawer reopen, after the main process resized the
        // window behind our back (collapse to the handle, restore of the saved bounds):
        // drop the memoized height so the observer's initial fire always resyncs.
        lastSentHeight = 0;
        panelResizeObserver = new ResizeObserver(() => syncWindowHeight());
        panelResizeObserver.observe(element);
    }
});

// The flyout mounts and unmounts with the tab's state: the window follows its
// growth, and shrinks back when it folds.
watch(adacFlyoutRef, (flyout) => {
    flyoutResizeObserver?.disconnect();
    flyoutResizeObserver = null;

    if (flyout) {
        flyoutResizeObserver = new ResizeObserver(() => syncWindowHeight());
        flyoutResizeObserver.observe(flyout);
    } else {
        syncWindowHeight();
    }
});

/**
 * Grow the window to the handle column when the column outgrows it.
 *
 * The column gains its ADAC entry after the window was first measured — the
 * configuration and the code porteur are read once the popup is up — and a
 * collapsed window is never measured again: the entry would stay cut at the
 * bottom. Grown, never shrunk: a collapsed window keeps its expanded bounds.
 */
const fitStripColumn = (): void => {
    const height = stripColumnRef.value?.offsetHeight ?? 0;

    if (height > window.innerHeight) {
        lastSentHeight = height;
        window.ipcRenderer.resizeNotificationWindow(height);
    }
};

watch(stripColumnRef, (element) => {
    stripResizeObserver?.disconnect();
    stripResizeObserver = null;

    if (element) {
        stripResizeObserver = new ResizeObserver(fitStripColumn);
        stripResizeObserver.observe(element);
    }
});

onBeforeUnmount(() => {
    panelResizeObserver?.disconnect();
    panelResizeObserver = null;
    flyoutResizeObserver?.disconnect();
    flyoutResizeObserver = null;
    stripResizeObserver?.disconnect();
    stripResizeObserver = null;
    window.removeEventListener('mousemove', reconcileClickThrough);
    window.removeEventListener('mouseout', releaseClickThrough);
    setClickThrough(false);
    stopCursorPoll();
});

/**
 * The window keeps its expanded bounds at all times (fixed-bounds mode): its
 * transparent areas let clicks fall through to the LGO. The mode is
 * reconciled STATELESSLY: point over the drawer subtree — panel, strip,
 * Vitale tab, collapsed handle — means solid, anywhere else means
 * click-through. A missed reconciliation self-heals on the next one, unlike
 * enter/leave pairs which can get out of sync when the cursor crosses
 * windows quickly.
 *
 * Two sources drive it, because a click-through window is DEAF to its own
 * mouse messages and must be told where the cursor is:
 *  - solid: the window's own `mousemove`, delivered natively;
 *  - click-through: the OS cursor position, polled through the main process.
 *
 * The shell also forwards mouse moves while ignoring (`forward: true`), and
 * those feed the same `mousemove` handler when they arrive — but they are a
 * bonus, never the only way back. Electron implements that forwarding with a
 * global low-level mouse hook, which stays silent whenever the cursor is
 * moved by SetCursorPos instead of real mouse input (ScreenConnect and other
 * remote-control tools) and which Windows silently drops when the hook
 * callback times out on a loaded machine. Depending on it left the window
 * transparent forever: alive, detecting and rendering patients, but deaf to
 * every click.
 */
const drawerRef = ref<HTMLElement | null>(null);
let clickThrough: boolean | null = null;

/** Cursor poll period while click-through, in ms. */
const CURSOR_POLL_INTERVAL = 100;
let cursorPoll: ReturnType<typeof setInterval> | null = null;
let pollInFlight = false;

/**
 * Whether a point, in client coordinates, lands on actual drawer UI. Floating
 * content (dropdown menus, popovers, tooltips) is teleported to <body>, outside
 * the drawer subtree, yet must stay solid or its clicks would fall through to
 * the LGO.
 */
const isOverDrawer = (x: number, y: number): boolean => {
    const element = document.elementFromPoint(x, y);

    return !!element && (
        !!drawerRef.value?.contains(element)
        || !!element.closest('[data-reka-popper-content-wrapper]')
    );
};

const setClickThrough = (ignore: boolean) => {
    // Never go click-through mid-drag: the drag relies on a captured pointer
    // whose events must keep reaching this window.
    const value = ignore && !store.draggingWindow;

    if (!supportsClickThrough() || clickThrough === value) {
        return;
    }

    clickThrough = value;
    window.ipcRenderer.setNotificationClickThrough!(value);

    // Blind from now on: watch the cursor to know when to become solid again.
    if (value) {
        startCursorPoll();
    } else {
        stopCursorPoll();
    }
};

/**
 * Reconcile from the OS cursor position instead of a mouse event.
 *
 * `getScreenContext` is a plain GetCursorPos on the main side: it reports the
 * cursor whatever moved it, with no hook in the way. The window bounds come
 * from the same process, so both points are in the same screen space and the
 * subtraction gives the client coordinates the hit test expects.
 */
const pollCursor = async () => {
    if (pollInFlight) {
        return;
    }

    pollInFlight = true;

    try {
        const [context, bounds] = await Promise.all([
            window.ipcRenderer.getScreenContext!(),
            window.ipcRenderer.getNotificationWindowBounds!(),
        ]);

        // The poll is async: the state may have flipped while it was in flight.
        if (!context || !bounds || clickThrough !== true) {
            return;
        }

        setClickThrough(!isOverDrawer(context.cursor.x - bounds.x, context.cursor.y - bounds.y));
    } catch {
        // Main process unreachable: keep the current state, retry next tick.
    } finally {
        pollInFlight = false;
    }
};

const startCursorPoll = () => {
    if (cursorPoll) {
        return;
    }

    cursorPoll = setInterval(pollCursor, CURSOR_POLL_INTERVAL);
    pollCursor();
};

const stopCursorPoll = () => {
    if (!cursorPoll) {
        return;
    }

    clearInterval(cursorPoll);
    cursorPoll = null;
};

/**
 * Reconcile from a mouse event: native while solid, forwarded by the shell
 * while click-through.
 *
 * In dev, `localStorage.setItem('ignoreForwardedMouse', '1')` mutes the
 * forwarded ones — reproducing a post whose mouse hook never fires, and
 * leaving the cursor poll alone in charge of becoming solid again.
 */
const reconcileClickThrough = (event: MouseEvent) => {
    const forwarded = clickThrough === true;

    if (forwarded && import.meta.env.DEV && localStorage.getItem('ignoreForwardedMouse') === '1') {
        return;
    }

    setClickThrough(!isOverDrawer(event.clientX, event.clientY));
};

/** Cursor left the window entirely: nothing to hover, let clicks through. */
const releaseClickThrough = (event: MouseEvent) => {
    if (!event.relatedTarget) {
        setClickThrough(true);
    }
};

window.addEventListener('mousemove', reconcileClickThrough);
window.addEventListener('mouseout', releaseClickThrough);

// Armed from the start: click-through until the cursor reaches actual UI.
setClickThrough(true);

/**
 * The consumer whose programs are displayed. Defaults to the detected consumer, so a
 * detected beneficiary is shown first; the header allows switching between the
 * beneficiary and the insured customer.
 */
const selectedConsumerKey = ref<string | null>(null);

watch(patientKey, () => {
    selectedConsumerKey.value = props.item?.detectedConsumer?.key ?? null;
}, {immediate: true});

/**
 * The consumers the pharmacist can switch between. The switch is only offered when the
 * detected consumer is a beneficiary: a detected customer keeps the historical display
 * (every consumer's programs, grouped by person).
 */
const switchableConsumers = computed<Consumer[]>(() => {
    if (props.item?.detectedConsumer?.type !== 'beneficiary' || !props.item.customer) {
        return [];
    }

    return [props.item.detectedConsumer, props.item.customer];
});

const selectedConsumer = computed<Consumer | null>(() => {
    return switchableConsumers.value.find((consumer) => consumer.key === selectedConsumerKey.value) ?? null;
});

/**
 * Whether the panel shows the detected person — the one ADAC is asked about,
 * by their NIR. Switched to someone else of the file, the ADAC card steps
 * aside: their answer must never read as another person's.
 */
const adacOnDetectedConsumer = computed(() => !selectedConsumer.value
    || selectedConsumer.value.key === (props.item?.detectedConsumer?.key ?? null));

const alternateConsumer = computed<Consumer | null>(() => {
    return switchableConsumers.value.find((consumer) => consumer.key !== selectedConsumerKey.value) ?? null;
});

/**
 * Switch the displayed consumer, then resize the window on the new content height.
 */
const switchConsumer = async () => {
    if (!alternateConsumer.value) {
        return;
    }

    selectedConsumerKey.value = alternateConsumer.value.key;

    await nextTick();
    handleNotificationResize();
};

/**
 * Reset the per-poll answered flags for each new detection. This component
 * outlives the panel (which unmounts when the drawer closes), so it is the
 * only reliable place to observe every new patient.
 */
watch(patientKey, () => store.resetPollState());

/**
 * Normalize the open window width on every open (the width never changes on
 * hover: NOTIFICATION_WINDOW_WIDTH always covers the fully slid-out tab —
 * resizing the transparent window mid-hover repaints it brutally on Windows
 * and invalidates the hover). The resize anchors the pinned screen edge
 * (extra arg, ignored by older apps which always anchor the right edge).
 */
watch(() => store.isOpen, (isOpen) => {
    if (isOpen) {
        window.ipcRenderer.send('notification-window-width', NOTIFICATION_WINDOW_WIDTH, store.edge);

        // Reopening restores the bounds saved when the drawer collapsed, which may not
        // match the content anymore. When the panel never unmounted (quick reopen), the
        // ResizeObserver stays silent: force one measurement through.
        lastSentHeight = 0;
        nextTick().then(syncWindowHeight);
    }
});

/**
 * Once the panel is hidden, collapse the window on the rendered handle column
 * height: its content varies (logo, programs badge, Vitale card bubble).
 */
watch(() => store.shouldShowPanel, async (shouldShowPanel) => {
    if (shouldShowPanel) {
        return;
    }

    await nextTick();

    // Fixed-bounds mode: the window keeps its expanded bounds while collapsed
    // (click-through neutralizes the transparent area), the strip just stays
    // glued to the pinned edge — nothing to resize, nothing to memorize.
    if (supportsClickThrough()) {
        return;
    }

    // 35px: the strip is w-10 with the app's 14px root font size (2.5rem).
    const height = stripColumnRef.value?.offsetHeight || DETECTION_COLLAPSED_HEIGHT;

    if (!supportsWindowPlacement()) {
        // Older app: the main process collapses on the right display edge itself.
        window.ipcRenderer.notificationToggle(false, {width: COLLAPSED_WIDTH, height});
        return;
    }

    const bounds = await window.ipcRenderer.getNotificationWindowBounds!();

    if (!bounds) {
        return;
    }

    // Memorize the expanded bounds to restore them when the drawer reopens.
    store.expandedBounds = bounds;

    const context = await window.ipcRenderer.getScreenContext!({
        x: bounds.x + Math.round(bounds.width / 2),
        y: bounds.y + Math.round(bounds.height / 2),
    });

    window.ipcRenderer.setNotificationWindowBounds!(
        collapsedAnchoredBounds(context.display, store.edge, bounds, {width: COLLAPSED_WIDTH, height})
    );
}, {immediate: true});

watch(() => [props.item, props.error], () => {
    const settings = props.item?.settings ?? props.error?.settings;

    if (!settings) {
        return;
    }

    // Plain errors never force the drawer open; the candidate picker must,
    // since it asks the pharmacist to act.
    if (props.error && !props.item && !props.error.candidates?.length) {
        return;
    }

    if (store.toggleTimeout) {
        clearTimeout(store.toggleTimeout);
        store.toggleTimeout = null;
    }

    if (store.closeOperationTimeout) {
        clearTimeout(store.closeOperationTimeout);
        store.closeOperationTimeout = null;
    }

    store.isOpen = true;
    store.shouldShowPanel = true;

    store.initToggleTimeout(settings);
}, {
    immediate: true
});

/**
 * Show customer page in main window.
 * @param customer
 */
const showCustomer = (customer: Customer) => {
    window.ipcRenderer.click();

    if (customer && customer.id) {
        window.ipcRenderer.send('to-main-window', 'show-customer', customer.id);
    }
};

/**
 * Open the « Prendre un rendez-vous » hub for the customer, in the main window.
 *
 * This window cannot host it — it is sized to its own content — so the main
 * window is sent to the patient file with the flag the file opens the hub on.
 * The `show-customer` route rather than `navigate`: the latter reloads the
 * whole renderer.
 */
const navigateAppointment = (customer: Customer) => {
    if (!customer?.id) {
        return;
    }

    window.ipcRenderer.click();
    window.ipcRenderer.send('to-main-window', 'show-customer', customer.id, {[APPOINTMENT_SHORTCUT_QUERY_KEY]: '1'});
};

/**
 * Open the counter scan in the main window, on the patient file with the
 * dialog already up. Like « Gérer » for loyalty cards, the pop-up stays open
 * while the team scans in the main window.
 */
const scanBirthdayGift = () => {
    const customerId = props.item?.customer?.id;

    if (!customerId) {
        return;
    }

    window.ipcRenderer.click();
    window.ipcRenderer.send('to-main-window', 'show-customer', customerId, {[BIRTHDAY_GIFT_SCAN_QUERY_KEY]: '1'});
};

/**
 * The membership screen path of a detected card, when it is activated.
 */
const membershipPath = (loyaltyCard: DetectionLoyaltyCard): string | null => {
    if (!loyaltyCard.membership || !props.item?.customer?.id) {
        return null;
    }

    return `/customer/${props.item.customer.id}/loyalty/membership/${loyaltyCard.membership.id}`;
};

/**
 * Open the membership page in main window.
 */
const showMembership = (loyaltyCard: DetectionLoyaltyCard) => {
    const path = membershipPath(loyaltyCard);

    if (path) {
        window.ipcRenderer.send('navigate', path);
    }
};

/**
 * Same screen, with the reward attribution dialog already open: the popup's
 * "Offrir" button is a shortcut to the offer, not a second "Gérer".
 */
const showReward = (loyaltyCard: DetectionLoyaltyCard) => {
    const path = membershipPath(loyaltyCard);

    if (path) {
        window.ipcRenderer.send('navigate', `${path}?reward=1`);
    }
};

/**
 * Attach a loyalty card to the customer.
 *
 * @param {Object} loyaltyCard The loyalty card.
 */
const attachLoyaltyCard = async (loyaltyCard: DetectionLoyaltyCard): Promise<void> => {
    if (!props.item?.customer?.id) {
        return Promise.resolve();
    }

    return await loyaltyCardMembershipApi()
        .create(loyaltyCard.id, props.item.customer.id)
        .then((membership) => {
            const card = props.item?.loyalty_cards.find((card) => card.id === loyaltyCard.id);

            if (card) {
                card.membership = membership;
            }
        });
};

/**
 * Navigate to the customer certification page.
 */
const navigateCertification = (customer: Customer) => {
    if (!customer || !customer.id) {
        return;
    }

    window.ipcRenderer.click();
    window.ipcRenderer.send(
        'navigate',
        `/customer/${customer.id}/certification`,
        false
    );
};

/**
 * Navigate to the customer survey page.
 *
 * @param {Object} customer
 */
const navigateSurveys = (customer: Customer) => {
    if (!customer || !customer.id) {
        return;
    }

    window.ipcRenderer.click();
    window.ipcRenderer.send(
        'navigate',
        `/customer/${customer.id}/survey-instances`,
        false
    );
};

/**
 * Navigate to the knowledgeable page.
 */
const navigateKnowledgeables = () => {
    window.ipcRenderer.click();
    window.ipcRenderer.send('navigate', '/knowledgeables', false);
};
</script>

<style scoped>
.notification-drawer {
    transition: all 0.3s ease-out;
}

.notification-open {
    transform: translateX(0);
}

/* The 35px kept visible must match COLLAPSED_WIDTH in the placement service. */
.notification-close-right {
    transform: translateX(calc(100% - 35px));
}

.notification-close-left {
    transform: translateX(calc(-100% + 35px));
}

/* The panel stays wheel/trackpad-scrollable, but never shows a scrollbar. */
.notification-scroll {
    scrollbar-width: none;
}

.notification-scroll::-webkit-scrollbar {
    display: none;
}
</style>
